import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import {
  Warehouse, Building2, Truck, Plus, Edit2, Trash2, MapPin,
  Package, AlertTriangle, Loader, X, Save, Star, Check
} from 'lucide-react';
import { useModal } from './AppModal';
import { useTheme } from './ThemeContext';

const locationTypes = [
  { id: 'warehouse', label: 'Warehouse', icon: Warehouse, description: 'Main storage facility' },
  { id: 'office', label: 'Office', icon: Building2, description: 'Office or showroom' },
  { id: 'truck', label: 'Truck', icon: Truck, description: 'Service vehicle' }
];

export default function LocationsPage({ companyId, onNavigate }) {
  const { colors } = useTheme();
  const { showConfirm, showSuccess, showError } = useModal();

  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [stockSummaries, setStockSummaries] = useState({});

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    location_type: 'truck',
    address: '',
    is_default: false,
    notes: ''
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (companyId) {
      loadLocations();
    } else {
      setLoading(false);
    }
  }, [companyId]);

  const loadLocations = async () => {
    try {
      setLoading(true);

      // Load locations
      const { data: locationData, error } = await supabase
        .from('locations')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('is_default', { ascending: false })
        .order('name');

      if (error) throw error;
      setLocations(locationData || []);

      // Load stock summaries for each location
      if (locationData && locationData.length > 0) {
        const { data: stockData } = await supabase
          .from('inventory_stock')
          .select(`
            location_id,
            quantity_on_hand,
            inventory_items!inner(reorder_point, unit_cost)
          `)
          .eq('company_id', companyId);

        // Aggregate by location
        const summaries = {};
        (stockData || []).forEach(stock => {
          if (!summaries[stock.location_id]) {
            summaries[stock.location_id] = {
              itemCount: 0,
              totalValue: 0,
              lowStockCount: 0
            };
          }
          summaries[stock.location_id].itemCount++;
          summaries[stock.location_id].totalValue +=
            (stock.quantity_on_hand || 0) * (stock.inventory_items?.unit_cost || 0);

          if (stock.quantity_on_hand <= (stock.inventory_items?.reorder_point || 0)) {
            summaries[stock.location_id].lowStockCount++;
          }
        });

        setStockSummaries(summaries);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      showError('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingLocation(null);
    setFormData({
      name: '',
      location_type: 'truck',
      address: '',
      is_default: locations.length === 0,
      notes: ''
    });
    setShowModal(true);
  };

  const openEditModal = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name || '',
      location_type: location.location_type || 'truck',
      address: location.address || '',
      is_default: location.is_default || false,
      notes: location.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (location) => {
    if (location.is_default) {
      showError('Cannot delete the default location. Set another location as default first.');
      return;
    }

    const summary = stockSummaries[location.id];
    const hasStock = summary?.itemCount > 0;

    const message = hasStock
      ? `Delete "${location.name}"? This location has ${summary.itemCount} item(s) in stock. The stock records will also be removed.`
      : `Delete "${location.name}"? This cannot be undone.`;

    const confirmed = await showConfirm(message, { confirmStyle: 'danger' });
    if (!confirmed) return;

    try {
      await supabase
        .from('locations')
        .update({ is_active: false })
        .eq('id', location.id);

      showSuccess('Location deleted');
      loadLocations();
    } catch (error) {
      showError('Failed to delete location');
    }
  };

  const handleSetDefault = async (location) => {
    if (location.is_default) return;

    try {
      // Clear current default
      await supabase
        .from('locations')
        .update({ is_default: false })
        .eq('company_id', companyId)
        .eq('is_default', true);

      // Set new default
      await supabase
        .from('locations')
        .update({ is_default: true })
        .eq('id', location.id);

      showSuccess(`${location.name} is now the default location`);
      loadLocations();
    } catch (error) {
      showError('Failed to update default location');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showError('Name is required');
      return;
    }

    setSaving(true);
    try {
      const locationData = {
        name: formData.name.trim(),
        location_type: formData.location_type,
        address: formData.address.trim() || null,
        is_default: formData.is_default,
        notes: formData.notes.trim() || null
      };

      // If setting as default, clear other defaults first
      if (formData.is_default) {
        await supabase
          .from('locations')
          .update({ is_default: false })
          .eq('company_id', companyId)
          .eq('is_default', true);
      }

      if (editingLocation) {
        const { error } = await supabase
          .from('locations')
          .update({
            ...locationData,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingLocation.id);

        if (error) throw error;
        showSuccess('Location updated');
      } else {
        const { error } = await supabase
          .from('locations')
          .insert({
            company_id: companyId,
            ...locationData
          });

        if (error) throw error;
        showSuccess('Location added');
      }

      setShowModal(false);
      loadLocations();
    } catch (error) {
      console.error('Error saving location:', error);
      showError('Failed to save location: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const getLocationIcon = (type) => {
    const locType = locationTypes.find(t => t.id === type);
    return locType?.icon || MapPin;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '50vh',
        color: colors.textMuted
      }}>
        <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: '1rem' }}>Loading locations...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div>
          <h1 style={{ margin: 0, color: colors.text, fontSize: isMobile ? '1.5rem' : '2rem' }}>
            <MapPin size={isMobile ? 24 : 32} style={{ marginRight: '0.75rem', verticalAlign: 'middle' }} />
            Locations
          </h1>
          <p style={{ margin: '0.5rem 0 0', color: colors.textMuted }}>
            Manage warehouses, offices, and trucks
          </p>
        </div>
        <button
          onClick={openCreateModal}
          style={{
            padding: '0.75rem 1.5rem',
            background: colors.accent,
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            justifyContent: 'center'
          }}
        >
          <Plus size={20} />
          Add Location
        </button>
      </div>

      {/* Locations Grid */}
      {locations.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: colors.textMuted
        }}>
          <MapPin size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
          <p style={{ margin: 0 }}>No locations yet</p>
          <button
            onClick={openCreateModal}
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              background: colors.accent,
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Add Your First Location
          </button>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '1rem'
        }}>
          {locations.map(location => {
            const Icon = getLocationIcon(location.location_type);
            const summary = stockSummaries[location.id] || { itemCount: 0, totalValue: 0, lowStockCount: 0 };

            return (
              <div
                key={location.id}
                style={{
                  background: colors.bgCard,
                  borderRadius: '12px',
                  padding: '1.25rem',
                  border: location.is_default
                    ? `2px solid ${colors.accent}`
                    : `1px solid ${colors.border}`
                }}
              >
                {/* Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '10px',
                      background: `${colors.accent}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon size={24} color={colors.accent} />
                    </div>
                    <div>
                      <h3 style={{ margin: 0, color: colors.text, fontSize: '1.1rem' }}>
                        {location.name}
                        {location.is_default && (
                          <Star size={14} color={colors.accent} style={{ marginLeft: '0.5rem', verticalAlign: 'middle' }} fill={colors.accent} />
                        )}
                      </h3>
                      <p style={{ margin: '0.25rem 0 0', color: colors.textMuted, fontSize: '0.85rem' }}>
                        {locationTypes.find(t => t.id === location.location_type)?.label || 'Location'}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => openEditModal(location)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        color: colors.textMuted,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(location)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        color: colors.error,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Address */}
                {location.address && (
                  <p style={{
                    margin: '0 0 1rem',
                    color: colors.textMuted,
                    fontSize: '0.9rem'
                  }}>
                    {location.address}
                  </p>
                )}

                {/* Stock Summary */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  background: colors.bgInput,
                  borderRadius: '8px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: colors.text, fontWeight: 'bold', fontSize: '1.1rem' }}>
                      {summary.itemCount}
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: '0.75rem' }}>Items</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      color: summary.lowStockCount > 0 ? colors.warning : colors.text,
                      fontWeight: 'bold',
                      fontSize: '1.1rem'
                    }}>
                      {summary.lowStockCount}
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: '0.75rem' }}>Low Stock</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: colors.text, fontWeight: 'bold', fontSize: '1.1rem' }}>
                      ${summary.totalValue.toFixed(0)}
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: '0.75rem' }}>Value</div>
                  </div>
                </div>

                {/* Actions */}
                <div style={{
                  marginTop: '1rem',
                  display: 'flex',
                  gap: '0.5rem'
                }}>
                  <button
                    onClick={() => onNavigate && onNavigate('inventory', { locationId: location.id })}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text,
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <Package size={14} /> View Stock
                  </button>
                  {!location.is_default && (
                    <button
                      onClick={() => handleSetDefault(location)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '6px',
                        color: colors.accent,
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}
                    >
                      <Star size={14} /> Set Default
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: colors.bgCard,
              borderRadius: '16px',
              width: '100%',
              maxWidth: '450px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h2 style={{ margin: 0, color: colors.text, fontSize: '1.25rem' }}>
                {editingLocation ? 'Edit Location' : 'Add Location'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
              {/* Location Type */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontWeight: 'bold'
                }}>
                  Location Type *
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '0.5rem'
                }}>
                  {locationTypes.map(type => {
                    const Icon = type.icon;
                    const isSelected = formData.location_type === type.id;
                    return (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, location_type: type.id })}
                        style={{
                          padding: '1rem 0.5rem',
                          background: isSelected ? `${colors.accent}20` : colors.bgInput,
                          border: isSelected ? `2px solid ${colors.accent}` : `1px solid ${colors.border}`,
                          borderRadius: '8px',
                          color: isSelected ? colors.accent : colors.text,
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        <Icon size={24} />
                        <span style={{ fontSize: '0.85rem', fontWeight: isSelected ? 'bold' : 'normal' }}>
                          {type.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Name */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontWeight: 'bold'
                }}>
                  Location Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={
                    formData.location_type === 'truck' ? "e.g., Service Van 1" :
                    formData.location_type === 'warehouse' ? "e.g., Main Warehouse" :
                    "e.g., Main Office"
                  }
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text
                  }}
                />
              </div>

              {/* Address */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.textMuted,
                  fontSize: '0.9rem'
                }}>
                  Address (optional)
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Main St, City, State"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text
                  }}
                />
              </div>

              {/* Is Default */}
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem',
                background: colors.bgInput,
                borderRadius: '8px',
                cursor: 'pointer',
                marginBottom: '1.25rem'
              }}>
                <input
                  type="checkbox"
                  checked={formData.is_default}
                  onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer'
                  }}
                />
                <div>
                  <div style={{ color: colors.text, fontWeight: 'bold' }}>
                    Set as default location
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                    Used as the default for inventory and job planning
                  </div>
                </div>
              </label>

              {/* Notes */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.textMuted,
                  fontSize: '0.9rem'
                }}>
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional notes..."
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={saving}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: colors.accent,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {saving ? (
                  <>
                    <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    {editingLocation ? 'Update Location' : 'Add Location'}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
